import { Box } from "@mui/material";
import * as React from "react";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as storeActions from "../../../models/store/storeActions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { TaskDynamicForm } from "../../task/TaskDynamicForm/TaskDynamicForm";

interface ITaskDataRowSaveResult {
    taskDataRow: ITaskDataRow;
    rowId: string;
}

export interface IRecordEditSheetDataRowDialogProp {
    classes?: string;
    taskType: ITaskEditTypeItem;
    item: ITaskDataRow;
    hasPrevious: boolean;
    hasNext: boolean;
    saveToDb: (taskDataRow: ITaskDataRow) => Promise<SaveSuccess>;
    onSaved: (item: ITaskDataRow, rowId: string, closeDialog: boolean) => void;
    onClose: (refreshList: boolean) => void;
    onPrevious: () => void;
    onNext: () => void;

}

export interface IRecordEditSheetDataRowDialogState {
    rowId: string;
    cols: ITaskDataRowCol[];
}

export class RecordEditSheetDataRowDialog extends React.Component<IRecordEditSheetDataRowDialogProp, IRecordEditSheetDataRowDialogState> {
    private orgStateHash: string = "";
        
    getStateFromProps = (props: IRecordEditSheetDataRowDialogProp): IRecordEditSheetDataRowDialogState => {
        const item = props.item;
        return {
            rowId: item.rowId,
            cols: item.cols.map(i => new TaskDataRowCol(i))
        };
    };

    getHashFromState = (props: IRecordEditSheetDataRowDialogProp, state: IRecordEditSheetDataRowDialogState): string => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        for (const col of state.cols) {
            result.append(col.field, (col.valueNbr ?? 0).toString(10) + "#" + (col.valueStr ?? ""));
        }
        return result.hash;
    };

    constructor(props: IRecordEditSheetDataRowDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    async componentDidUpdate(prevProps: IRecordEditSheetDataRowDialogProp, prevState: IRecordEditSheetDataRowDialogState) {
        const props = this.props;
        if (prevProps.item.getHash() === props.item.getHash()) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getHashFromState(props, state);
    }

    handleRefreshTaskDataRowsForm = (taskDataRowCols: ITaskDataRowCol[]) => {
        this.setState({ cols: taskDataRowCols });
    };

    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            TaskTypeCol.validateInputOfRequiredTaskTypeCols(props.taskType.formCols, state.cols, null,
                (typeCol: ITaskTypeCol) => {
                    store.customStore.dispatch(storeActions.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title)));
                    return resolve(false);
                });
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<ITaskDataRowSaveResult> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        // Set data
        const taskDataRow = new TaskDataRow(props.item);
        taskDataRow.rowId = state.rowId;
        taskDataRow.cols = state.cols.slice(0);
        // Call server
        const result = await props.saveToDb(taskDataRow);
        if (!result) return null;
        //Update state and hash
        const stateChange = { rowId: result.rowId };
        this.orgStateHash = this.getHashFromState(props, { ...state, ...stateChange });
        this.setState(stateChange);
        return {
            taskDataRow,
            rowId: result.rowId
        };
    };

    saveDialog = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onSaved(result.taskDataRow, result.rowId, closeDialog);
        }
        return result;
    };

    handleSave = async (closeDialog: boolean): Promise<ITaskDataRow> => {
        const result = await this.saveDialog(closeDialog);
        if (!result) return null;
        return result.taskDataRow;
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveDialog(false),
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; });
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        return (
            <Dialog
                id="record-edit-sheet-data-row-dialog"
                maxWidth="md"
                title={Translations.RowInformation}
                body={
                    <Box sx={{ margin: "-0.5rem -1rem -1rem -1rem" }}>
                        <TaskDynamicForm
                            isReadOnly={false}
                            taskTypeCols={props.taskType.formCols}
                            taskDataRowCols={state.cols}
                            onRefreshCols={this.handleRefreshTaskDataRowsForm}
                        />
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent={isNew ? "flex-end" : "space-between"} width="100%">
                        {!isNew && (
                            <DialogItemsNav
                                hasPrevious={props.hasPrevious}
                                hasNext={props.hasNext}
                                onPrevious={this.handlePrevious}
                                onNext={this.handleNext}
                            />
                        )}
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}